import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import socketService from '../services/SocketService';

const SocketContext = createContext(null);

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

export const SocketProvider = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [roomId, setRoomId] = useState(null);
    const [playerId, setPlayerId] = useState(null);
    const [connectionError, setConnectionError] = useState(null);

    useEffect(() => {
        const updateState = () => {
            setIsConnected(socketService.isSocketConnected());
            setRoomId(socketService.getRoomId());
            setPlayerId(socketService.getPlayerId());
        };

        updateState();

        const roomInfoUpdateCleanup = socketService.onRoomInfoUpdate((roomId, playerId) => {
            setRoomId(roomId);
            setPlayerId(playerId);
        });

        const setupSocketListeners = () => {
            if (socketService.getSocket()) {
                socketService.on('connect', () => {
                    setIsConnected(true);
                    setConnectionError(null);
                    updateState();
                });

                socketService.on('disconnect', () => {
                    setIsConnected(false);
                    updateState();
                });

                socketService.on('connect_error', (error) => {
                    setConnectionError(error.message);
                    setIsConnected(false);
                    updateState();
                });
            }
        };

        if (socketService.getSocket()) {
            setupSocketListeners();
        } else {
            const checkSocket = () => {
                if (socketService.getSocket()) {
                    setupSocketListeners();
                } else {
                    setTimeout(checkSocket, 100);
                }
            };
            checkSocket();
        }

        return () => {
            if (socketService.getSocket()) {
                socketService.off('connect');
                socketService.off('disconnect');
                socketService.off('connect_error');
            }
            roomInfoUpdateCleanup();
        };
    }, []);

    const value = useMemo(() => {
        const connect = () => {
            try {
                socketService.connect();
                setConnectionError(null);
            } catch (error) {
                setConnectionError(error.message);
            }
        };

        const disconnect = () => {
            socketService.disconnect();
            setIsConnected(false);
            setRoomId(null);
            setPlayerId(null);
        };

        const createRoom = async (nickname, category, gameType = 'liar') => {
            if (!socketService.isSocketConnected()) {
                connect();
            }
            const result = await socketService.createRoom(nickname, category, gameType);
            setRoomId(result.room_id);
            setPlayerId(result.player_id);
            return result;
        };

        const joinRoom = async (roomCode, nickname) => {
            if (!socketService.isSocketConnected()) {
                connect();
            }
            const result = await socketService.joinRoom(roomCode, nickname);
            setRoomId(result.room.room_id);
            setPlayerId(result.player_id || Date.now().toString());
            return result;
        };

        const leaveRoom = () => {
            socketService.leaveRoom();
            setRoomId(null);
            setPlayerId(null);
        };

        return {
            isConnected,
            roomId,
            playerId,
            connectionError,
            connect,
            disconnect,
            createRoom,
            joinRoom,
            leaveRoom,
            socketService,
            startGame: () => socketService.startGame(),
            submitDescription: (text) => socketService.submitDescription(text),
            startVote: () => socketService.startVote(),
            submitVote: (targetId) => socketService.submitVote(targetId),
            submitLastChance: (guessWord) => socketService.submitLastChance(guessWord),
            on: (event, callback) => socketService.on(event, callback),
            off: (event) => socketService.off(event),
            emit: (event, data, callback) => socketService.emit(event, data, callback)
        };
    }, [isConnected, roomId, playerId, connectionError]);

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
